import queryString from 'query-string';
import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import ArrowDownIcon from '../../../images/svg/arrow-down.svg';
import { useSiteSearch } from './provider';
import CloseButton from '../../primitives/close-button';
import Container from '../../primitives/grid/container';
import { Overlay, OverlayContent } from '../../primitives/overlay';
import Col from '../../primitives/grid/col';
import Row from '../../primitives/grid/row';
import Typography from '../../primitives/typography';
import { colors } from '../../primitives/colors';
import VisuallyHidden from '../../primitives/visually-hidden';
import useDynamicImports, { LoadingStrategy } from '../../../hooks/use-dynamic-imports';
import { IndiciesInfo, SiteSearchState } from './types';

const FILTERS = [
    { id: 'all', name: 'All', indexName: null },
    { id: 'pages', name: 'SERVPRO.com', indexName: 'search_pages_v2_dev' },
    { id: 'services', name: 'Services', indexName: 'search_services_v2_dev' },
    { id: 'articles', name: 'Articles', indexName: 'search_articles_v2_dev' },
    { id: 'press_releases', name: 'Press Releases', indexName: 'search_press_v2_dev' },
    { id: 'faqs', name: 'FAQs', indexName: 'search_faqs_v2_dev' },
    {
        id: 'glossary_terms',
        name: 'Glossary Terms',
        indexName: 'search_glossaries_v2_dev',
    },
];

const styles = {
    siteSearchTitle: css({
        margin: '48px 0',
    }),
    siteSearchWrap: css({
        maxWidth: '100%',
        width: '100%',
        height: '100vh',
    }),
    closeButtonWrap: css({
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
    }),
    noResults: css({
        fontSize: '18px',
        lineHeight: '28px',
        fontWeight: 500,
    }),
    results: css({
        marginTop: '40px',
        '@media (min-width: 1024px)': {
            marginTop: '64px',
        },
        '> :not([hidden]) ~ :not([hidden])': {
            marginTop: '64px',
        },
    }),
    optionsInnerWrap: css({
        marginBottom: '8px',
        gap: '8px',
        borderBottomWidth: '1px',
        '@media (min-width: 1024px)': {
            display: 'flex',
            paddingLeft: '8px',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            borderBottomWidth: '0',
            '>:not([hidden])~:not([hidden])': {
                marginLeft: '20px',
            },
        },
    }),
    optionsOuterWrap: css({
        visibility: 'hidden',
        position: 'absolute',
        left: '0',
        top: '100%',
        marginTop: '8px',
        width: '100%',
        '@media (min-width: 1024px)': {
            position: 'static',
            visibility: 'visible',
        },
        '>:not([hidden])~:not([hidden])': {
            marginLeft: '20px',
        },
        '&[data-dropdown-open="true"]': {
            visibility: 'visible',
        },
    }),
    dropdownOptionWrap: css({
        borderTopWidth: '1px',
        borderRightWidth: '1px',
        borderLeftWidth: '1px',
        backgroundColor: colors.shadesWhite,
        color: 'inherit',
        pointerEvents: 'auto',
        '@media (min-width: 1024px)': {
            backgroundImage: 'none',
            borderLeftWidth: '0',
            borderRightWidth: '0',
            borderTopWidth: '0',
        },
        '&[data-active="true"]': {
            color: colors.primaryOrange,
            pointerEvents: 'none',
        },
    }),
    dropdownOption: css({
        width: '100%',
        padding: '8px 16px',
        borderRadius: '4px',
        fontSize: '14px',
        lineHeight: '20px',
        textAlign: 'left',
        '@media (min-width: 1024px)': {
            padding: '0 12px',
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 500,
        },
        ':focus': {
            boxShadow: '0 0 0 2px rgba(96, 165, 250, 1)',
        },
    }),
    dropdownWrap: css({
        position: 'relative',
        zIndex: 30,
    }),
    dropdownArrowWrap: css({
        display: 'flex',
        paddingLeft: '16px',
        paddingRight: '16px',
        marginBottom: '8px',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderRadius: '4px',
        borderWidth: '1px',
        height: '40px',
        fontSize: '14px',
        lineHeight: '20px',
        backgroundColor: '#ffffff',
        '@media (min-width: 1024px)': {
            display: 'none',
        },
    }),
    dropdownArrow: css({
        color: colors.primaryOrange,
        transition: 'transform .25s',
        transform: 'rotate(0deg)',
        path: {
            fill: 'currentColor',
        },
        '&[data-dropdown-open="true"]': {
            transform: 'rotate(-180deg)',
        },
    }),
};

const SiteSearch: React.FC = (): JSX.Element => {
    const { visible, setVisible } = useSiteSearch();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchQueryKey = 'siteSearch';

    // Dynamic imports for search functionality
    const {
        hasLoaded,
        scripts: {
            algoliaClient,
            Configure,
            InstantSearch,
            SearchInput,
            Index,
            Results,
        },
        load,
    } = useDynamicImports({
        algoliaClient: async () => {
            const search = await import('algoliasearch/lite');
            return search.liteClient(
                'SXQTSMSLU9',
                'ee5fa3035d420655ffa33ec9bd348ac1',
            );
        },
        Configure: async () => {
            const { Configure: cfg } = await import('react-instantsearch');
            return cfg;
        },
        InstantSearch: async () => {
            const { InstantSearch: instantSearch } = await import('react-instantsearch');
            return instantSearch;
        },
        Index: async () => {
            const { Index: index } = await import('react-instantsearch');
            return index;
        },
        Results: async () => {
            const { default: results } = await import('./results');
            return results;
        },
        SearchInput: async () => {
            const { default: searchInput } = await import('./search-input');
            return searchInput;
        },
    }, LoadingStrategy.Interaction);

    // State management using useReducer pattern for related state
    const [state, setState] = React.useState<SiteSearchState>({
        currentFilterId: 'all',
        indicesInfo: FILTERS.slice(1).reduce<IndiciesInfo>((acc, curr) => {
            if (curr && curr.indexName) {
                return {
                    ...acc,
                    [curr.indexName]: {
                        isEmpty: false,
                    },
                };
            }
            return acc;
        }, {}),
        filterDropdownOpened: false,
    });

    // Memoized values
    const activeFilter = useMemo(
        () => FILTERS.find(item => item.id === state.currentFilterId),
        [state.currentFilterId],
    );

    const isEmpty = useCallback((): boolean => (
        Object.keys(state.indicesInfo).every(key => state.indicesInfo[key]?.isEmpty)
    ), [state.indicesInfo]);

    // URL management
    useEffect(() => {
        const parsedQueryString = queryString.parse(window.location.search);
        if (parsedQueryString[searchQueryKey]) {
            setVisible(true);
        }
    }, [setVisible, searchQueryKey]);

    useEffect(() => {
        const parsedQueryString = queryString.parse(window.location.search);

        if (visible) {
            const searchPart = queryString.stringify({
                ...parsedQueryString,
                [searchQueryKey]: true,
            });
            if (!parsedQueryString[searchQueryKey]) {
                window.history.pushState(
                    '',
                    'Site Search',
                    `${window.location.pathname}?${searchPart}`,
                );
            }
        } else if (parsedQueryString[searchQueryKey]) {
            const querySearchPart = queryString.stringify(
                Object.keys(parsedQueryString).reduce((acc, key) => {
                    if (key === searchQueryKey) {
                        return acc;
                    }
                    return {
                        ...acc,
                        [key]: parsedQueryString[key],
                    };
                }, {}),
            );
            window.history.pushState(
                '',
                '',
                `${window.location.pathname}${querySearchPart ? `?${querySearchPart}` : ''}`,
            );
        }
    }, [visible, searchQueryKey]);

    // Load search scripts when modal opens
    useEffect(() => {
        if (visible && !hasLoaded) {
            load();
        }
    }, [visible, hasLoaded, load]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setState(prev => ({ ...prev, filterDropdownOpened: false }));
            }
        };

        if (state.filterDropdownOpened) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [state.filterDropdownOpened]);

    // Close dropdown on escape key
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && state.filterDropdownOpened) {
                setState(prev => ({ ...prev, filterDropdownOpened: false }));
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [state.filterDropdownOpened]);

    // Event handlers
    const handleFilterChange = useCallback((filterId: string): void => {
        setState(prev => ({
            ...prev,
            currentFilterId: filterId,
            filterDropdownOpened: false,
        }));
    }, []);

    const toggleDropdown = useCallback((): void => {
        setState(prev => ({
            ...prev,
            filterDropdownOpened: !prev.filterDropdownOpened,
        }));
    }, []);

    const handleCloseModal = useCallback(() => {
        setVisible(false);
        setState(prev => ({
            ...prev,
            filterDropdownOpened: false,
        }));
    }, [setVisible]);

    return (
        <Overlay open={visible} onOpenChange={setVisible}>
            <OverlayContent
                css={styles.siteSearchWrap}
                onOpenAutoFocus={e => e.preventDefault()}
                onCloseAutoFocus={e => e.preventDefault()}
            >
                <DialogPrimitive.Title asChild>
                    <VisuallyHidden>Site Search</VisuallyHidden>
                </DialogPrimitive.Title>
                <DialogPrimitive.Description asChild>
                    <VisuallyHidden>Search for content across SERVPRO.com</VisuallyHidden>
                </DialogPrimitive.Description>

                <Container>
                    <Row css={styles.siteSearchTitle}>
                        <Col breakpoints={{ dt: { span: 10 }, mb: { span: 3 } }}>
                            <Typography
                                fontSize={{
                                    dt: '3xl',
                                    tb: 'xl',
                                }}
                                fontWeight="medium"
                                as="h2"
                            >
                                How can we help you?
                            </Typography>
                        </Col>
                        <Col
                            breakpoints={{ dt: { span: 2 }, mb: { span: 1 } }}
                            css={styles.closeButtonWrap}
                        >
                            <DialogPrimitive.Close asChild>
                                <CloseButton onClick={handleCloseModal} />
                            </DialogPrimitive.Close>
                        </Col>
                    </Row>

                    <div css={styles.dropdownWrap} ref={dropdownRef}>
                        <button
                            type="button"
                            css={styles.dropdownArrowWrap}
                            onClick={toggleDropdown}
                            aria-expanded={state.filterDropdownOpened}
                            aria-haspopup="listbox"
                            aria-label="Select search category"
                        >
                            {activeFilter ? activeFilter.name : 'All'}
                            <ArrowDownIcon css={styles.dropdownArrow} data-dropdown-open={state.filterDropdownOpened} />
                        </button>
                        <div
                            css={styles.optionsOuterWrap}
                            data-dropdown-open={state.filterDropdownOpened}
                        >
                            <div css={styles.optionsInnerWrap} role="listbox" aria-label="Search categories">
                                {FILTERS.map(item => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        css={[styles.dropdownOptionWrap, styles.dropdownOption]}
                                        data-active={item.id === state.currentFilterId}
                                        onClick={() => handleFilterChange(item.id)}
                                        role="option"
                                        aria-selected={item.id === state.currentFilterId}
                                    >
                                        {item.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {hasLoaded && (
                        <InstantSearch
                            indexName="search_services"
                            searchClient={algoliaClient}
                            future={{ preserveSharedStateOnUnmount: true }}
                        >
                            <SearchInput />

                            <div css={styles.results}>
                                {isEmpty() && <div css={styles.noResults}>No results</div>}
                                {FILTERS.filter((filter) => {
                                    if (filter.id === 'all') {
                                        return false;
                                    }
                                    if (state.currentFilterId === 'all') {
                                        return true;
                                    }
                                    return filter.id === state.currentFilterId;
                                }).map((filter) => {
                                    if (!filter.indexName) return null;
                                    return (
                                        <div key={filter.id}>
                                            <Index indexName={filter.indexName}>
                                                <Results
                                                    title={filter.name}
                                                    indexName={filter.indexName}
                                                    setIndicesInfo={(value) => {
                                                        setState(prev => ({
                                                            ...prev,
                                                            indicesInfo: typeof value === 'function' ? value(prev.indicesInfo) : value,
                                                        }));
                                                    }}
                                                />
                                                <Configure
                                                    hitsPerPage={state.currentFilterId === 'all' ? 6 : 18}
                                                    attributesToSnippet={['title', 'description', 'content']}
                                                    distinct
                                                />
                                            </Index>
                                        </div>
                                    );
                                })}
                            </div>
                        </InstantSearch>
                    )}
                </Container>
            </OverlayContent>
        </Overlay>
    );
};

export default SiteSearch;
